import { FormGroup } from '@angular/forms';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCheckCircle,
  faGaugeMax,
  faListCheck,
  faListDropdown,
  faInputPipe,
  faInputText,
  faRectangleVerticalHistory,
  faBan,
  faImage,
  faMemo,
  faStar,
  faUpload,
  faChartSimpleHorizontal,
  faRankingStar
} from '@fortawesome/pro-regular-svg-icons';
import { ExtendedRankSortQuestionType, ExtendedRatingQuestionType, QuestionType } from '@empathy/survey/base';

import { NotepadQuestionForm } from './notepad-form.model';
import { NotepadQuestion } from './notepad-model.model';
import { ExtendedQuestionType, NotepadAvailableTypes } from './notepad-question-type.constant';
import { SurveyUtils } from '../utils/utils';

export const NotepadQuestionTypeConfig: Record<NotepadAvailableTypes, INotepadQuestionTypeConfig> = {
  [QuestionType.SingleChoice]: {
    type: QuestionType.SingleChoice,
    icon: faCheckCircle,
    text: 'Single Choice',
    factory: SurveyUtils.questionWithOptionsFactory,
    mandatoryFlag: true,
    otherOptionFlag: true,
    randomizeFlag: true,
    noneOptionFlag: false
  },
  [QuestionType.MultiChoice]: {
    type: QuestionType.MultiChoice,
    icon: faListCheck,
    text: 'Multi Choice',
    factory: SurveyUtils.questionWithOptionsFactory,
    mandatoryFlag: true,
    otherOptionFlag: true,
    randomizeFlag: true,
    noneOptionFlag: false
  },
  [QuestionType.Essay]: {
    type: QuestionType.Essay,
    icon: faInputText,
    text: 'Open Ended Question',
    factory: SurveyUtils.questionFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [QuestionType.Carousel]: {
    type: QuestionType.Carousel,
    icon: faRectangleVerticalHistory,
    text: 'Carousel',
    factory: SurveyUtils.questionCarouselFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [QuestionType.CarouselMultiChoice]: {
    type: QuestionType.CarouselMultiChoice,
    icon: faRectangleVerticalHistory,
    text: 'Carousel Multi Choice',
    factory: SurveyUtils.questionCarouselFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [QuestionType.InfoPage]: {
    type: QuestionType.InfoPage,
    icon: faMemo,
    text: 'Info Page',
    factory: SurveyUtils.questionFactory,
    mandatoryFlag: false,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [QuestionType.Input]: {
    type: QuestionType.Input,
    icon: faInputPipe,
    text: 'Input Variable',
    factory: SurveyUtils.questionFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [QuestionType.Dropdown]: {
    type: QuestionType.Dropdown,
    icon: faListDropdown,
    text: 'Dropdown',
    factory: SurveyUtils.questionWithOptionsFactory,
    mandatoryFlag: true,
    otherOptionFlag: true,
    randomizeFlag: true,
    noneOptionFlag: false
  },
  [ExtendedRatingQuestionType.RatingNumeric]: {
    type: ExtendedRatingQuestionType.RatingNumeric,
    icon: faStar,
    text: 'Rating - Numeric',
    factory: SurveyUtils.questionNumericRatingFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [ExtendedRatingQuestionType.RatingDescriptive]: {
    type: ExtendedRatingQuestionType.RatingDescriptive,
    icon: faStar,
    text: 'Rating - Descriptive',
    factory: SurveyUtils.questionDescriptiveRatingFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [ExtendedRatingQuestionType.RatingStars]: {
    type: ExtendedRatingQuestionType.RatingStars,
    icon: faStar,
    text: 'Rating - Stars',
    factory: SurveyUtils.questionStarsRatingFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [QuestionType.Heatmap]: {
    type: QuestionType.Heatmap,
    icon: faImage,
    text: 'Heatmap',
    factory: SurveyUtils.questionFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [QuestionType.FileUpload]: {
    type: QuestionType.FileUpload,
    icon: faUpload,
    text: 'File Upload',
    factory: SurveyUtils.questionFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [QuestionType.Terminate]: {
    type: QuestionType.Terminate,
    icon: faBan,
    text: 'Terminate',
    factory: SurveyUtils.questionFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [QuestionType.NPS]: {
    type: QuestionType.NPS,
    icon: faGaugeMax,
    text: 'NPS',
    factory: SurveyUtils.questionFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: false,
    noneOptionFlag: false
  },
  [QuestionType.Ranking]: {
    type: QuestionType.Ranking,
    icon: faChartSimpleHorizontal,
    text: 'Ranking Question',
    factory: SurveyUtils.questionWithOptionsFactory,
    mandatoryFlag: true,
    otherOptionFlag: false,
    randomizeFlag: true,
    noneOptionFlag: false
  },
  [ExtendedRankSortQuestionType.RankSortNumeric]: {
    type: ExtendedRankSortQuestionType.RankSortNumeric,
    icon: faRankingStar,
    text: 'Rank sort - Numeric',
    factory: SurveyUtils.questionNumericRankSortFactory,
    mandatoryFlag: true,
    otherOptionFlag: true,
    randomizeFlag: false,
    noneOptionFlag: true
  },
  [ExtendedRankSortQuestionType.RankSortDescriptive]: {
    type: ExtendedRankSortQuestionType.RankSortDescriptive,
    icon: faRankingStar,
    text: 'Rank sort - Descriptive',
    factory: SurveyUtils.questionDescriptiveRankSortFactory,
    mandatoryFlag: true,
    otherOptionFlag: true,
    randomizeFlag: false,
    noneOptionFlag: true
  }
};

interface INotepadQuestionTypeConfig {
  type: ExtendedQuestionType;
  icon: IconDefinition;
  text: string;
  factory: (questionId: string, type: QuestionType, previousData: FormGroup<NotepadQuestionForm>) => NotepadQuestion;
  mandatoryFlag: boolean;
  randomizeFlag: boolean;
  otherOptionFlag: boolean;
  noneOptionFlag: boolean;
}
